import { useState } from 'react';
import { useAuthentication } from '../../auth';
import { DateRangePickerInput } from '../../components/Input/DateRangePickerInput';
import { Slider } from '../../components/Input/Slider';
import { Tile } from '../../components/Tile';
import { InternalDashboardSkeleton } from '../components/InternalDashboardSkeleton';
import { InternalTabsList } from '../components/InternalTabsList';
import { UTILIZATION_METRICS, UtilizationMetric } from '../components/utilizationMetrics';
import { UtilizationMetricSelect } from '../components/UtilizationMetricSelect';
import { UtilizationPivotTable } from '../components/UtilizationPivotTable';
import { useUtilization } from '../hooks/useUtilization';

export interface UtilizationTabProps {
  dateRange: [Date, Date] | null;
  onDateRangeChange: (range: [Date, Date] | null) => void;
}

type ThresholdsByMetric = Partial<Record<UtilizationMetric, [number, number]>>;

export function UtilizationTab({ dateRange, onDateRangeChange }: UtilizationTabProps) {
  const [metric, setMetric] = useState<UtilizationMetric>('activePatients');
  const [thresholdsByMetric, setThresholdsByMetric] = useState<ThresholdsByMetric>({});
  const currentUser = useAuthentication();
  const { data: utilization, isFetching } = useUtilization(dateRange, true);

  const config = UTILIZATION_METRICS[metric];
  const thresholds: [number, number] | undefined = config.ragDefaults
    ? (thresholdsByMetric[metric] ?? [config.ragDefaults.red, config.ragDefaults.amber])
    : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-4 md:flex-row md:justify-between">
        <InternalTabsList />
        <div className="flex gap-4">
          <DateRangePickerInput
            value={dateRange}
            onChange={onDateRangeChange}
            className="w-[350px]"
            testId="date-range-input"
          />
        </div>
      </div>
      <Tile title="">
        <div className="mb-8 flex flex-col items-center gap-6 md:flex-row md:gap-8">
          <UtilizationMetricSelect value={metric} onValueChange={setMetric} />
          {config.sliderRange && thresholds && (
            <Slider
              min={config.sliderRange.min}
              max={config.sliderRange.max}
              step={config.sliderRange.step}
              value={thresholds}
              onValueChange={(values) =>
                setThresholdsByMetric((prev) => ({ ...prev, [metric]: [values[0], values[1]] }))
              }
              valueFormatter={(values, index) => config.format(values[index])}
              className="w-full max-w-xs"
            />
          )}
        </div>
        {isFetching ? (
          <InternalDashboardSkeleton />
        ) : (
          <UtilizationPivotTable
            utilization={utilization}
            subtenants={currentUser.subtenants}
            metric={metric}
            thresholds={thresholds ? { red: thresholds[0], amber: thresholds[1] } : undefined}
          />
        )}
      </Tile>
    </div>
  );
}
